import { tripSheetAssignmentStatusesEnum } from "helpers/enums/tripSheetAssignmentStatuses";
import { tripSheetFactSourcesEnum } from "helpers/enums/tripSheetFactSources";
import { tripSheetInspectionRecordMethodsEnum } from "helpers/enums/tripSheetInspectionRecordMethods";
import { tripSheetMedicalInspectionResultsEnum } from "helpers/enums/tripSheetMedicalInspectionResults";
import { tripSheetMedicalInspectionTypesEnum } from "helpers/enums/tripSheetMedicalInspectionTypes";
import { tripSheetRouteOperationTypesEnum } from "helpers/enums/tripSheetRouteOperationTypes";
import { tripSheetTechnicalInspectionResultsEnum } from "helpers/enums/tripSheetTechnicalInspectionResults";
import { tripSheetTechnicalInspectionTypesEnum } from "helpers/enums/tripSheetTechnicalInspectionTypes";

const DATE_FIELD_NAMES = new Set([
    "validFrom",
    "validTo",
    "documentDate",
    "plannedDepartureAt",
    "actualDepartureAt",
    "plannedReturnAt",
    "actualReturnAt",
    "preTripInspectionAt",
    "medicalExamAt",
    "medicalInspectionAt",
    "plannedStartedAt",
    "plannedEndedAt",
    "actualStartedAt",
    "actualEndedAt",
    "odometerOnStartFixedAt",
    "odometerOnEndFixedAt",
    "inspectedAt",
    "plannedArrivalAt",
    "actualArrivalAt",
    "actualOperationStartedAt",
    "actualOperationEndedAt",
    "arrivalTime",
    "departureTime",
]);

const NUMERIC_FIELD_NAMES = new Set([
    "tripCount",
    "plannedTripCount",
    "actualTripCount",
    "distance",
    "plannedDistance",
    "plannedDistanceKm",
    "actualDistance",
    "weight",
    "plannedWeight",
    "actualWeight",
    "odometerValueOnDeparture",
    "odometerValueOnReturn",
    "odometerOnStart",
    "odometerOnEnd",
    "actualMileage",
    "sequenceNumber",
    "driverShiftId",
    "startRoutePointId",
    "endRoutePointId",
    "transferredByDriverId",
    "acceptedByDriverId",
    "issuedLiters",
    "balanceDepartureLiters",
    "refilledOnRouteLiters",
    "balanceReturnLiters",
    "normChangeCoefficient",
]);

const ROUTE_POINT_REFERENCE_FIELD_NAMES = new Set(["startRoutePointId", "endRoutePointId", "transferRoutePointId"]);

const isEmpty = (value) => value === "" || value === undefined;

/** Convert all known date fields returned by the API to organization-local form values. */
const normalizeDateTree = (value, dateForForm) => {
    if (Array.isArray(value)) {
        return value.map((item) => normalizeDateTree(item, dateForForm));
    }
    if (!value || typeof value !== "object") {
        return value;
    }

    return Object.entries(value).reduce((result, [key, item]) => {
        result[key] = DATE_FIELD_NAMES.has(key) && item ? dateForForm(item) : normalizeDateTree(item, dateForForm);
        return result;
    }, Object.create(null));
};

/** Convert form collections back to API dates and normalize optional numeric values without losing zeroes. */
const serializeTree = (value, dateForPayload) => {
    if (Array.isArray(value)) {
        return value.map((item) => serializeTree(item, dateForPayload));
    }
    if (!value || typeof value !== "object") {
        return value;
    }

    return Object.entries(value).reduce((result, [key, item]) => {
        if (DATE_FIELD_NAMES.has(key)) {
            result[key] = item ? dateForPayload(item) : null;
        } else if (ROUTE_POINT_REFERENCE_FIELD_NAMES.has(key)) {
            const routePointId = Number(item);
            result[key] = Number.isSafeInteger(routePointId) && routePointId > 0 ? routePointId : null;
        } else if (NUMERIC_FIELD_NAMES.has(key)) {
            const numericValue = Number(item);
            result[key] = isEmpty(item) || item === null ? null : Number.isNaN(numericValue) ? item : numericValue;
        } else if (item === "") {
            result[key] = null;
        } else {
            result[key] = serializeTree(item, dateForPayload);
        }
        return result;
    }, Object.create(null));
};

const getAssignmentPoints = (assignment) => assignment?.routePoints || [];

const withAssignmentPoints = (assignment, routePoints) => ({ ...assignment, routePoints });

const pointAddress = (point) => point?.address || "";

/** Present legacy DriverId fields as shifts without inventing a second driver's period or mileage. */
const legacyDriverShifts = (data) => {
    if (!data.driverId && !data.secondaryDriverId) {
        return [];
    }

    const primaryShift = data.driverId
        ? {
              sequenceNumber: 1,
              driverId: data.driverId,
              isCrew: !!data.isCrew,
              plannedStartedAt: data.plannedDepartureAt || data.validFrom || null,
              plannedEndedAt: data.plannedReturnAt || data.validTo || null,
              actualStartedAt: data.actualDepartureAt || null,
              actualEndedAt: data.actualReturnAt || null,
              odometerOnStart: data.odometerValueOnDeparture ?? null,
              odometerOnEnd: data.odometerValueOnReturn ?? null,
              odometerOnStartSource: data.departureOdometerSource || null,
              odometerOnEndSource: data.returnOdometerSource || null,
              odometerOnStartCorrectionReason: data.departureOdometerCorrectionReason || null,
              odometerOnEndCorrectionReason: data.returnOdometerCorrectionReason || null,
              medicalInspections: data.medicalExamAt
                  ? [
                        {
                            type: tripSheetMedicalInspectionTypesEnum.PreTrip,
                            isRequired: true,
                            inspectedAt: data.medicalExamAt,
                            result: tripSheetMedicalInspectionResultsEnum.NotSpecified,
                            recordMethod: tripSheetInspectionRecordMethodsEnum.Migration,
                        },
                    ]
                  : [],
          }
        : null;
    const secondaryShift = data.secondaryDriverId
        ? {
              sequenceNumber: primaryShift ? 2 : 1,
              driverId: data.secondaryDriverId,
              isCrew: !!data.isCrew,
              requiresClarification: true,
              isDrivingPeriodKnown: false,
              medicalInspections: [],
          }
        : null;

    return [primaryShift, secondaryShift].filter(Boolean);
};

/** Present a legacy PreTripInspectionAt as an event with an intentionally unknown result. */
const legacyTechnicalInspections = (data) =>
    data.preTripInspectionAt
        ? [
              {
                  type: tripSheetTechnicalInspectionTypesEnum.PreTrip,
                  inspectedAt: data.preTripInspectionAt,
                  result: tripSheetTechnicalInspectionResultsEnum.NotSpecified,
                  responsibleFullName: data.mechanicName,
                  recordMethod: tripSheetInspectionRecordMethodsEnum.Migration,
              },
          ]
        : [];

const normalizeTechnicalInspection = (inspection) => ({
    ...inspection,
    result:
        inspection.result === tripSheetMedicalInspectionResultsEnum.Admitted
            ? tripSheetTechnicalInspectionResultsEnum.ReleaseAllowed
            : inspection.result === tripSheetMedicalInspectionResultsEnum.NotAdmitted
              ? tripSheetTechnicalInspectionResultsEnum.ReleaseNotAllowed
              : inspection.result,
});

/** Build an assignment from the complete bid payload, retaining all route points when the endpoint supplies them. */
const assignmentFromBid = (bid) => {
    const bidPoints = bid.points || [];
    const firstPoint = bidPoints[0] || bid.firstBidPoint;
    const lastPoint = bidPoints[bidPoints.length - 1] || bid.lastBidPoint;
    const points = bidPoints.map((point, index) => ({
        bidPointId: point.id,
        sequenceNumber: index + 1,
        address: point.address,
        latitude: point.latitude ?? null,
        longitude: point.longitude ?? null,
        operationType:
            {
                LoadPoint: tripSheetRouteOperationTypesEnum.Loading,
                UnloadPoint: tripSheetRouteOperationTypesEnum.Unloading,
                ServicePoint: tripSheetRouteOperationTypesEnum.Control,
                StartPoint: tripSheetRouteOperationTypesEnum.Intermediate,
            }[point.type] || tripSheetRouteOperationTypesEnum.Other,
        plannedArrivalAt: point.arrivalAt || null,
        plannedDepartureAt: point.departureAt || null,
        actualArrivalAt: point.actualArrivalAt || null,
        actualDepartureAt: point.actualDepartureAt || null,
        factSource: point.actualArrivalAt || point.actualDepartureAt ? tripSheetFactSourcesEnum.Bid : null,
    }));

    return {
        bidId: bid.id,
        bidNumber: bid.number,
        counterpartyDetails: bid.counterparty?.details || "",
        loadingAddress: pointAddress(firstPoint),
        arrivalTime: firstPoint?.arrivalAt || "",
        unloadingAddress: pointAddress(lastPoint),
        departureTime: lastPoint?.departureAt || "",
        cargoDetails:
            bid.cargos
                ?.map((cargo) => cargo.details)
                .filter(Boolean)
                .join(", ") || "",
        tripCount: 1,
        plannedTripCount: 1,
        distance: bid.planDistanceKm ?? "",
        plannedDistance: bid.planDistanceKm ?? null,
        weight: bid.planWeight ?? null,
        plannedWeight: bid.planWeight ?? null,
        status: tripSheetAssignmentStatusesEnum.Planned,
        routePoints: points,
    };
};

const getPersonName = (person) => person?.fullName || person?.name || person?.driverFullName || "";

export {
    DATE_FIELD_NAMES,
    assignmentFromBid,
    getAssignmentPoints,
    getPersonName,
    legacyDriverShifts,
    legacyTechnicalInspections,
    normalizeDateTree,
    normalizeTechnicalInspection,
    pointAddress,
    serializeTree,
    withAssignmentPoints,
};
